import { HttpClient } from '@angular/common/http'
import { inject, Injectable } from '@angular/core'

import { Observable } from 'rxjs'

import {
  Protocol,
  ProtocolCreateRequest,
  ProtocolStep,
  ProtocolStepCreateRequest,
  ProtocolStepInstructionFile,
  ProtocolStepInstructionFileUpdateRequest,
  ProtocolStepUpdateRequest,
  ProtocolUpdateRequest
} from '@yoshu-lib/models/protocol.model'

import { cleanHttpParams } from '@yoshu-lib/utils/clean-http-params'

import { environment } from '@coty/env/environment'

@Injectable({ providedIn: 'root' })
export class ProtocolsService {
  private http = inject(HttpClient)
  private baseUrl = `${environment.apiUrl}/protocols`

  // Protocol CRUD operations
  getProtocols(token?: string): Observable<Protocol[]> {
    return this.http.get<Protocol[]>(this.baseUrl, {
      params: cleanHttpParams({ token })
    })
  }

  getProtocol(protocolId: string, token?: string): Observable<Protocol> {
    return this.http.get<Protocol>(`${this.baseUrl}/${protocolId}`, {
      params: cleanHttpParams({ token })
    })
  }

  createProtocol(body: ProtocolCreateRequest, token?: string): Observable<Protocol> {
    return this.http.post<Protocol>(this.baseUrl, body, {
      params: cleanHttpParams({ token })
    })
  }

  updateProtocol(
    protocolId: string,
    body: ProtocolUpdateRequest,
    token?: string
  ): Observable<Protocol> {
    return this.http.patch<Protocol>(`${this.baseUrl}/${protocolId}`, body, {
      params: cleanHttpParams({ token })
    })
  }

  deleteProtocol(protocolId: string, token?: string): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/${protocolId}`, {
      params: cleanHttpParams({ token })
    })
  }

  // Protocol Step operations
  createProtocolStep(
    protocolId: string,
    body: ProtocolStepCreateRequest,
    token?: string
  ): Observable<ProtocolStep> {
    return this.http.post<ProtocolStep>(`${this.baseUrl}/${protocolId}/steps`, body, {
      params: cleanHttpParams({ token })
    })
  }

  updateProtocolStep(
    protocolId: string,
    stepId: string,
    body: ProtocolStepUpdateRequest,
    token?: string
  ): Observable<ProtocolStep> {
    return this.http.patch<ProtocolStep>(`${this.baseUrl}/${protocolId}/steps/${stepId}`, body, {
      params: cleanHttpParams({ token })
    })
  }

  deleteProtocolStep(protocolId: string, stepId: string, token?: string): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/${protocolId}/steps/${stepId}`, {
      params: cleanHttpParams({ token })
    })
  }

  // Protocol Step Instruction File operations
  uploadInstructionFile(
    protocolId: string,
    stepId: string,
    file: File,
    includedInPrompt = false,
    token?: string
  ): Observable<ProtocolStepInstructionFile> {
    const formData = new FormData()
    formData.append('file', file)
    formData.append('included_in_prompt', includedInPrompt.toString())

    return this.http.post<ProtocolStepInstructionFile>(
      `${this.baseUrl}/${protocolId}/steps/${stepId}/files`,
      formData,
      {
        params: cleanHttpParams({ token })
      }
    )
  }

  deleteInstructionFile(
    protocolId: string,
    stepId: string,
    fileId: string,
    token?: string
  ): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/${protocolId}/steps/${stepId}/files/${fileId}`, {
      params: cleanHttpParams({ token })
    })
  }

  updateInstructionFile(
    protocolId: string,
    stepId: string,
    fileId: string,
    body: ProtocolStepInstructionFileUpdateRequest,
    token?: string
  ): Observable<ProtocolStepInstructionFile> {
    return this.http.patch<ProtocolStepInstructionFile>(
      `${this.baseUrl}/${protocolId}/steps/${stepId}/files/${fileId}`,
      body,
      {
        params: cleanHttpParams({ token })
      }
    )
  }
}
